import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatCNPJ } from "@/lib/format";
import { exportarFolhaBeneficio, type AlvoExportacao, type EmpresaDaFolha } from "@/components/beneficios/exportar";
import { ChevronDown, Download, FileArchive, FileSpreadsheet, Loader2 } from "lucide-react";

interface Props {
  folhaId: string;
  /** Sem a lista (tela de folhas), o menu mostra só "todas" e o .zip. */
  empresas?: EmpresaDaFolha[];
  disabled?: boolean;
  size?: "sm" | "default";
  variant?: "default" | "outline" | "ghost";
  /** Texto do botão; ignorado em `compacto`. */
  rotulo?: string;
  /** Só o ícone (linhas da tabela de folhas). */
  compacto?: boolean;
  onExportado?: () => void;
}

/** Exportar a planilha da Flash: todas as empresas, uma empresa, ou .zip com uma por empresa. */
export const ExportarMenu = ({
  folhaId,
  empresas,
  disabled,
  size = "default",
  variant = "default",
  rotulo = "Exportar para Flash",
  compacto = false,
  onExportado,
}: Props) => {
  const [exportando, setExportando] = useState(false);

  const exportar = async (alvo: AlvoExportacao) => {
    setExportando(true);
    if (await exportarFolhaBeneficio(folhaId, alvo)) onExportado?.();
    setExportando(false);
  };

  const icone = exportando ? <Loader2 className="animate-spin" /> : <Download />;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {compacto ? (
          <Button
            size="icon"
            variant={variant}
            className="h-8 w-8 text-muted-foreground"
            disabled={disabled || exportando}
            title="Exportar para Flash"
            aria-label="Exportar para Flash"
          >
            {icone}
          </Button>
        ) : (
          <Button size={size} variant={variant} disabled={disabled || exportando}>
            {icone}
            {rotulo}
            <ChevronDown className="opacity-80" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[300px]">
        <DropdownMenuItem onSelect={() => void exportar({ tipo: "todas" })}>
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Todas as empresas (1 planilha)
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void exportar({ tipo: "zip" })}>
          <FileArchive className="mr-2 h-4 w-4" />
          Uma planilha por empresa (ZIP)
        </DropdownMenuItem>

        {empresas && empresas.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Só uma empresa
            </DropdownMenuLabel>
            <div className="max-h-64 overflow-y-auto">
              {empresas.map((e) => (
                <DropdownMenuItem
                  key={e.cnpj}
                  onSelect={() => void exportar({ tipo: "empresa", cnpj: e.cnpj })}
                  className="flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="truncate">{e.nome}</p>
                    <p className="text-xs text-muted-foreground">{formatCNPJ(e.cnpj)}</p>
                  </div>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    {e.colaboradores} {e.colaboradores === 1 ? "pessoa" : "pessoas"}
                  </span>
                </DropdownMenuItem>
              ))}
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
